import React, { FC, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddLink,
  AttachFile,
  Chat as ChatIcon,
  CloudUpload,
  Delete,
  Description,
  Image as ImageIcon,
  LinkOff,
  OpenInNew,
  PictureAsPdf,
  TableChart,
} from "@mui/icons-material";

import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  useGetTariffEngineeringProductQuery,
  useGetProductAttachmentsQuery,
  useGenerateProductAttachmentUploadUrlMutation,
  useCreateProductAttachmentMutation,
  useDeleteProductAttachmentMutation,
  useLinkChatToProductMutation,
  useUnlinkChatFromProductMutation,
} from "@/services/tariff-engineering";
import type { ProductAttachment } from "@/types/tariff-engineering";
import { tokens } from "@/tokens";
import ChatPickerDialog from "@/pages/COMMON/tariff-engineering/ChatPickerDialog";

type Props = {
  productId: string;
  // When true, hide the standalone "linked chats" + "files" headers and
  // render compactly — used inside a dialog where the dialog itself
  // already carries the heading.
  compact?: boolean;
};

const ACCEPT =
  "application/pdf,image/png,image/jpeg,image/webp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const fileIcon = (mimeType: string) => {
  if (mimeType.startsWith("image/")) return <ImageIcon fontSize="small" />;
  if (mimeType === "application/pdf") return <PictureAsPdf fontSize="small" />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("csv"))
    return <TableChart fontSize="small" />;
  if (mimeType.includes("wordprocessing"))
    return <Description fontSize="small" />;
  return <AttachFile fontSize="small" />;
};

const fmtSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
};

const fmtRelativeDate = (s: string | null | undefined): string => {
  if (!s) return "";
  const d = new Date(s);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
};

const StatusChip: FC<{ status: ProductAttachment["status"] }> = ({ status }) => {
  const map: Record<
    ProductAttachment["status"],
    { label: string; color: string }
  > = {
    UPLOADED: { label: "Uploaded", color: tokens.text.muted },
    PROCESSING: { label: "Extracting…", color: tokens.status.warning },
    READY: { label: "Ready", color: tokens.status.success },
    FAILED: { label: "Failed", color: tokens.status.danger },
  };
  const s = map[status];
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        height: 18,
        fontSize: tokens.fontSize["2xs"],
        fontWeight: tokens.fontWeight.semibold,
        color: s.color,
        backgroundColor: `${s.color}14`,
        border: "none",
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
};

export const ProductContextPanel: FC<Props> = ({ productId, compact }) => {
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);

  const scopeArgs = useMemo(() => {
    const args: { tenantId?: string; clientId?: string } = {};
    const isAdmin = user?.userRole === roles.TDADMIN;
    const isTDAdminTCD =
      isAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
    if (selectedTenant?._id) args.tenantId = selectedTenant._id;
    if (selectedClient?._id) {
      args.clientId = selectedClient._id;
    } else if (isTDAdminTCD && selectedTenant?._id) {
      args.clientId = selectedTenant._id;
    }
    return args;
  }, [user, selectedTenant?._id, selectedTenant?.tenantType, selectedClient?._id]);

  // Linked chats live on the product itself — reuse the existing detail query
  const { data: product, refetch: refetchProduct } =
    useGetTariffEngineeringProductQuery(
      { productId, ...scopeArgs },
      { skip: !productId }
    );

  const { data: attachments = [], refetch: refetchAttachments } =
    useGetProductAttachmentsQuery(
      { productId, ...scopeArgs },
      { skip: !productId, pollingInterval: 5000 }
    );

  const [genUploadUrl] = useGenerateProductAttachmentUploadUrlMutation();
  const [createAttachment] = useCreateProductAttachmentMutation();
  const [deleteAttachment] = useDeleteProductAttachmentMutation();
  const [linkChat] = useLinkChatToProductMutation();
  const [unlinkChat] = useUnlinkChatFromProductMutation();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadingNames, setUploadingNames] = useState<Set<string>>(
    () => new Set()
  );
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingUnlinkId, setPendingUnlinkId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File) => {
    const tag = `${file.name}::${file.size}`;
    setUploadingNames((prev) => {
      const next = new Set(prev);
      next.add(tag);
      return next;
    });
    setUploadError(null);
    try {
      // Step 1: get signed URL
      const { signedUrl, s3Key } = await genUploadUrl({
        productId,
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        ...scopeArgs,
      }).unwrap();

      // Step 2: upload directly to S3 (PUT)
      const putRes = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed: ${putRes.status}`);
      }

      // Step 3: record metadata + kick off background extraction
      await createAttachment({
        productId,
        s3Key,
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        ...scopeArgs,
      }).unwrap();

      refetchAttachments();
    } catch (e: any) {
      setUploadError(e?.message || String(e));
    } finally {
      setUploadingNames((prev) => {
        const next = new Set(prev);
        next.delete(tag);
        return next;
      });
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(uploadFile);
  };

  const handleDelete = async (attachmentId: string) => {
    setPendingDeleteId(attachmentId);
    try {
      await deleteAttachment({
        attachmentId,
        productId,
        ...scopeArgs,
      }).unwrap();
      refetchAttachments();
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleLinkChat = async (chatId: string) => {
    await linkChat({ productId, chatId, ...scopeArgs }).unwrap();
    refetchProduct();
  };

  const handleUnlinkChat = async (chatId: string) => {
    setPendingUnlinkId(chatId);
    try {
      await unlinkChat({ productId, chatId, ...scopeArgs }).unwrap();
      refetchProduct();
    } finally {
      setPendingUnlinkId(null);
    }
  };

  const linkedChats = product?.linkedChats || [];
  const attachedChatIds = product?.attachedChatIds || [];

  // Compact mode (used inside a dialog) skips the Paper wrapper since the
  // dialog already provides one. Otherwise each section gets its own card.
  const Section: FC<{ children: React.ReactNode }> = ({ children }) =>
    compact ? <Box sx={{ mb: 2 }}>{children}</Box> : <Paper sx={{ p: 2 }}>{children}</Paper>;

  return (
    <Stack spacing={2}>
      {/* ─────────── Files & images ─────────── */}
      <Section>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachFile fontSize="small" color="action" />
            <Typography variant="h6">Files & images</Typography>
            <Chip
              size="small"
              label={attachments.length}
              sx={{ height: 20 }}
            />
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUpload fontSize="small" />}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            hidden
            multiple
            accept={ACCEPT}
            onChange={(e) => {
              handleFiles(e.target.files);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
        </Stack>

        {uploadError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {uploadError}
          </Alert>
        )}

        {uploadingNames.size > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Uploading {uploadingNames.size} file
              {uploadingNames.size === 1 ? "" : "s"}…
            </Typography>
            <LinearProgress sx={{ mt: 0.5 }} />
          </Box>
        )}

        {attachments.length === 0 && uploadingNames.size === 0 && (
          <Box
            sx={{
              border: `2px dashed ${tokens.border.default}`,
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 150ms",
              "&:hover": { borderColor: tokens.brand.primary },
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFiles(e.dataTransfer.files);
            }}
          >
            <CloudUpload fontSize="large" sx={{ color: tokens.text.muted }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Drop product images, spec sheets, or description docs here, or
              click to upload.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PDF · PNG · JPG · WEBP · XLSX · CSV · DOCX
            </Typography>
          </Box>
        )}

        {attachments.length > 0 && (
          <List dense disablePadding>
            {attachments.map((a) => (
              <ListItem
                key={a._id}
                disableGutters
                sx={{ alignItems: "flex-start", py: 0.5 }}
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    {a.accessUrl && (
                      <Tooltip title="Open">
                        <IconButton
                          size="small"
                          onClick={() =>
                            window.open(
                              a.accessUrl as string,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        disabled={pendingDeleteId === a._id}
                        onClick={() => handleDelete(a._id)}
                      >
                        {pendingDeleteId === a._id ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Delete fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemIcon sx={{ minWidth: 32, mt: 0.75 }}>
                  {fileIcon(a.mimeType)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap" }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {a.filename}
                      </Typography>
                      <StatusChip status={a.status} />
                      <Typography variant="caption" color="text.secondary">
                        {fmtSize(a.sizeBytes)} · {fmtRelativeDate(a.createdAt)}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    a.status === "FAILED" && a.error?.message ? (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {a.error.message}
                      </Typography>
                    ) : a.summary ? (
                      <Box
                        sx={{
                          mt: 0.5,
                          pl: 1,
                          borderLeft: "2px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {a.summary}
                        </Typography>
                      </Box>
                    ) : null
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Section>

      {/* ─────────── Linked chats ─────────── */}
      <Section>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ChatIcon fontSize="small" color="action" />
            <Typography variant="h6">Linked chats</Typography>
            <Chip
              size="small"
              label={linkedChats.length}
              sx={{ height: 20 }}
            />
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddLink fontSize="small" />}
            onClick={() => setPickerOpen(true)}
          >
            Link a chat
          </Button>
        </Stack>

        {linkedChats.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No chats linked yet. Attach a conversation so its context feeds
            into the next tariff-engineering run.
          </Typography>
        )}

        {linkedChats.length > 0 && (
          <List dense disablePadding>
            {linkedChats.map((c) => (
              <ListItem
                key={c._id}
                disableGutters
                sx={{ alignItems: "flex-start", py: 0.5 }}
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Open chat">
                      <IconButton
                        size="small"
                        onClick={() =>
                          window.open(
                            `/chat-advisor?chat=${c._id}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Unlink">
                      <IconButton
                        size="small"
                        disabled={pendingUnlinkId === c._id}
                        onClick={() => handleUnlinkChat(c._id)}
                      >
                        {pendingUnlinkId === c._id ? (
                          <CircularProgress size={14} />
                        ) : (
                          <LinkOff fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap" }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {c.title}
                      </Typography>
                      {c.category && (
                        <Chip
                          size="small"
                          label={c.category}
                          sx={{ height: 18 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {c.totalMessages} msg
                        {c.totalMessages === 1 ? "" : "s"}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    c.lastAiExcerpt ? (
                      <Box
                        sx={{
                          mt: 0.5,
                          pl: 1,
                          borderLeft: "2px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {c.lastAiExcerpt}
                        </Typography>
                      </Box>
                    ) : null
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Section>

      <ChatPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handleLinkChat}
        alreadyLinkedIds={attachedChatIds}
      />
    </Stack>
  );
};

export default ProductContextPanel;
